import React, { useEffect, useMemo, useState } from "react";
import { FiUnlock, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { unlockTimesheetSubmission } from "../../../services/timesheetApi";
import { getBangkokDateKey } from "../../../utils/timesheetUtils";

const REASON_MAX_LENGTH = 500;
const PAST_DATE_MESSAGE = "วันที่เปิดให้แก้ต้องเป็นวันนี้หรือวันถัดไป";

const formatThaiDate = (dateKey) => {
  if (!dateKey) return "-";
  const date = new Date(`${String(dateKey).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "numeric" });
};

const translateMessage = (message) =>
  message === "reopenUntil cannot be before today in Asia/Bangkok" ? PAST_DATE_MESSAGE : message;

// 400 จาก backend อาจมาเป็น errors (array/object ตาม field) หรือมีแค่ message
// ถ้าบอก field ไม่ได้ ให้ดูจากข้อความว่าพูดถึง reopenUntil หรือไม่ ไม่งั้นแสดงใต้ช่องเหตุผล
const getFieldErrors = (data) => {
  const rawErrors = data?.errors;
  const list = Array.isArray(rawErrors)
    ? rawErrors
    : rawErrors && typeof rawErrors === "object"
      ? Object.entries(rawErrors).map(([field, value]) => ({
          field,
          message: typeof value === "string" ? value : value?.message,
        }))
      : [];

  const errors = {};
  list.forEach((item) => {
    const field = item?.field || item?.path || item?.param;
    const message = item?.message || item?.msg;
    if ((field === "reason" || field === "reopenUntil") && message) {
      errors[field] = translateMessage(message);
    }
  });
  if (Object.keys(errors).length) return errors;

  const message = String(data?.message || "ข้อมูลไม่ถูกต้อง");
  return /reopenUntil/i.test(message)
    ? { reopenUntil: translateMessage(message) }
    : { reason: message };
};

const UnlockSubmissionModal = ({ submission, onClose, onUnlocked, onConflict }) => {
  const [reason, setReason] = useState("");
  const [reopenUntil, setReopenUntil] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const todayKey = useMemo(() => getBangkokDateKey(), []);

  useEffect(() => {
    if (!submission) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, submission, submitting]);

  if (!submission) return null;

  const user = submission.user || {};
  const employeeName = user.displayName || user.username || "-";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const errors = {};
    if (!reason.trim()) errors.reason = "กรุณาระบุเหตุผล";
    else if (reason.trim().length > REASON_MAX_LENGTH) errors.reason = `เหตุผลต้องไม่เกิน ${REASON_MAX_LENGTH} ตัวอักษร`;
    if (reopenUntil && reopenUntil < todayKey) errors.reopenUntil = PAST_DATE_MESSAGE;
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    try {
      setSubmitting(true);
      const result = await unlockTimesheetSubmission(submission._id, { reason, reopenUntil });
      const openUntil = result?.reopenUntil ?? result?.data?.reopenUntil ?? null;
      toast.success(
        openUntil
          ? `ปลดล็อกแล้ว เปิดให้ ${employeeName} แก้ไขได้ถึง ${formatThaiDate(openUntil)}`
          : `ปลดล็อกแล้ว ${employeeName} สามารถแก้ไขและส่งใหม่ได้`
      );
      onUnlocked(submission);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      if (status === 400) {
        setFieldErrors(getFieldErrors(data));
      } else if (status === 409) {
        toast.warning("รายการนี้ถูกปลดล็อกหรือเปลี่ยนสถานะไปแล้ว");
        onConflict(submission);
      } else if (status === 403) {
        toast.error("คุณไม่มีสิทธิ์ปลดล็อก Timesheet นี้");
      } else {
        toast.error(data?.message || "ไม่สามารถปลดล็อก Timesheet ได้");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (hasError) =>
    `mt-2 w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 disabled:opacity-60 dark:bg-gray-900 dark:text-gray-100 ${
      hasError
        ? "border-red-300 focus:ring-red-200 dark:border-red-700"
        : "border-gray-200 focus:ring-indigo-300 dark:border-gray-700"
    }`;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-labelledby="unlock-submission-title">
      <form onSubmit={handleSubmit} className="w-full max-w-lg rounded-3xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"><FiUnlock /></div>
            <div>
              <h2 id="unlock-submission-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">ปลดล็อก Timesheet</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">เปิดให้พนักงานกลับไปแก้ไขและส่งอนุมัติใหม่</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="ปิด" className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"><FiX size={18} /></button>
        </div>

        <div className="mt-5 rounded-2xl bg-gray-50 p-4 text-sm dark:bg-gray-900/50">
          <p className="text-gray-600 dark:text-gray-300">พนักงาน: <strong className="text-gray-900 dark:text-gray-100">{employeeName}</strong>{user.department ? ` (${user.department})` : ""}</p>
          <p className="mt-1 text-gray-600 dark:text-gray-300">สัปดาห์: <strong className="text-gray-900 dark:text-gray-100">{formatThaiDate(submission.periodStart)} - {formatThaiDate(submission.periodEnd)}</strong></p>
        </div>

        <label className="mt-5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          เหตุผล <span className="text-red-500">*</span>
          <textarea
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              if (fieldErrors.reason) setFieldErrors((prev) => ({ ...prev, reason: "" }));
            }}
            maxLength={REASON_MAX_LENGTH}
            rows={4}
            disabled={submitting}
            placeholder="เช่น ลงชั่วโมงผิดโปรเจกต์ ให้แก้ไขแล้วส่งใหม่"
            className={`${inputClass(Boolean(fieldErrors.reason))} resize-y`}
          />
        </label>
        <div className="mt-1 flex justify-between gap-3 text-xs">
          <span className="text-red-600 dark:text-red-400">{fieldErrors.reason}</span>
          <span className="shrink-0 text-gray-500 dark:text-gray-400">{reason.length}/{REASON_MAX_LENGTH}</span>
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300">
          เปิดให้แก้ถึงวันที่ (ไม่บังคับ)
          <input
            type="date"
            value={reopenUntil}
            min={todayKey}
            disabled={submitting}
            onChange={(event) => {
              setReopenUntil(event.target.value);
              if (fieldErrors.reopenUntil) setFieldErrors((prev) => ({ ...prev, reopenUntil: "" }));
            }}
            className={inputClass(Boolean(fieldErrors.reopenUntil))}
          />
        </label>
        {fieldErrors.reopenUntil && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{fieldErrors.reopenUntil}</p>}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">ใช้เฉพาะสัปดาห์ที่เลย deadline แล้ว ถ้าไม่ระบุจะเปิดให้ 7 วัน</p>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting} className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">ยกเลิก</button>
          <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"><FiUnlock />{submitting ? "กำลังปลดล็อก..." : "ยืนยันปลดล็อก"}</button>
        </div>
      </form>
    </div>
  );
};

export default UnlockSubmissionModal;
